import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminSettings } from '@system-atlas/shared';
import { ApiError, api, type UserRow } from '../lib/api';
import { Alert, Badge, Button, Card, Label, Select, Spinner } from '../components/ui';
import { useToast } from '../components/toast';

export default function AdminSettingsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [settings, setSettings] = useState<AdminSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setSettings(await api.getAdminSettings());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggleMfa = async (value: boolean) => {
    setBusy(true);
    try {
      setSettings(await api.setMfaGlobalRequired(value));
      toast.success(value ? t('settings.mfaEnabledToast') : t('settings.mfaDisabledToast'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-bold tracking-tight">{t('settings.title')}</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t('settings.subtitle')}</p>

      {error && (
        <div className="mt-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}

      {!settings ? (
        <div className="mt-6">
          <Spinner />
        </div>
      ) : (
        <>
          <Card className="mt-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-md">
                <h2 className="text-lg font-semibold">{t('settings.mfaTitle')}</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  {t('settings.mfaDescription')}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                {settings.mfaGlobalRequired || settings.mfaEnvForced ? (
                  <Badge tone="green">{t('settings.mfaEnforced')}</Badge>
                ) : (
                  <Badge tone="amber">{t('settings.mfaNotEnforced')}</Badge>
                )}
                <Button
                  variant={settings.mfaGlobalRequired ? 'secondary' : 'primary'}
                  disabled={busy || settings.mfaEnvForced}
                  onClick={() => void toggleMfa(!settings.mfaGlobalRequired)}
                >
                  {settings.mfaGlobalRequired ? t('settings.disable') : t('settings.enable')}
                </Button>
              </div>
            </div>
            {settings.mfaEnvForced && (
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{t('settings.envForcedHint')}</p>
            )}
          </Card>

          <AzureCard settings={settings} onSaved={setSettings} />
        </>
      )}
    </div>
  );
}

/** Card „Microsoft Azure": Status (SSO/SCIM) und SCIM-Deprovisionierungs-Policy. */
function AzureCard({ settings, onSaved }: { settings: AdminSettings; onSaved: (s: AdminSettings) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [action, setAction] = useState<'deactivate' | 'delete'>(settings.scimDeprovisionAction);
  const [fallback, setFallback] = useState<string | null>(settings.scimFallbackOwnerId);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .listUsers()
      .then(({ users: rows }) => setUsers(rows))
      .catch(() => undefined);
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const updated = await api.setScimSettings({ scimDeprovisionAction: action, scimFallbackOwnerId: fallback });
      onSaved(updated);
      setAction(updated.scimDeprovisionAction);
      setFallback(updated.scimFallbackOwnerId);
      toast.success(t('azure.savedToast'));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const inactive = !settings.ssoEnabled && !settings.scimEnabled;

  return (
    <Card className="mt-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-md">
          <h2 className="text-lg font-semibold">{t('azure.title')}</h2>
        </div>
        <div className="flex flex-col items-end gap-1 text-sm">
          <span className="flex items-center gap-2">
            {t('azure.ssoStatus')}
            {settings.ssoEnabled ? (
              <Badge tone="green">{t('azure.enabled')}</Badge>
            ) : (
              <Badge tone="amber">{t('azure.disabled')}</Badge>
            )}
          </span>
          <span className="flex items-center gap-2">
            {t('azure.scimStatus')}
            {settings.scimEnabled ? (
              <Badge tone="green">{t('azure.enabled')}</Badge>
            ) : (
              <Badge tone="amber">{t('azure.disabled')}</Badge>
            )}
          </span>
        </div>
      </div>

      {inactive && (
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{t('azure.enableViaEnvHint')}</p>
      )}

      <div className="mt-5 space-y-4 border-t border-slate-200 pt-4 dark:border-slate-700">
        <div>
          <Label htmlFor="scim-action">{t('azure.deprovisionAction')}</Label>
          <Select id="scim-action" value={action} onChange={(e) => setAction(e.target.value as 'deactivate' | 'delete')}>
            <option value="deactivate">{t('azure.deactivate')}</option>
            <option value="delete">{t('azure.delete')}</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="scim-fallback">{t('azure.fallbackOwner')}</Label>
          <Select
            id="scim-fallback"
            value={fallback ?? ''}
            onChange={(e) => setFallback(e.target.value || null)}
          >
            <option value="">{t('azure.noFallback')}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName} ({u.email})
              </option>
            ))}
          </Select>
          {action === 'delete' && !fallback && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{t('azure.fallbackHint')}</p>
          )}
        </div>
        <div className="flex justify-end">
          <Button disabled={busy} onClick={() => void save()}>
            {busy ? t('common.pleaseWait') : t('azure.save')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
